<template>
  <article
    class="article-detail"
    :style="{ paddingBottom: `${paddingBottom}px` }"
  >
    <template v-if="data">
      <div class="article-detail-main">
        <h2 class="article-detail-title ellipsis-2" :title="data.post_title">
          {{ data.post_title }}
        </h2>
        <div class="article-detail-info">
          <span>{{ data.published_time | YYYYMMDD }}</span>
          <span>{{ data.post_hits | crowd }}人已阅读</span>
          <span
            class="article-detail-info-platform"
            v-if="data.more && data.more.show_platform == '1'"
          >
            适用于：
            <span
              v-for="(item, index) in data.more.available_platform"
              :key="index"
            >
              {{ item }}
            </span>
          </span>
        </div>

        <div
          class="article-detail-content"
          ref="content"
          v-html="data.post_content"
        ></div>
      </div>
      <div
        class="article-detail-recommend"
        ref="recommend"
        v-show="isShowRecommend"
      >
        <div class="article-detail-recommend-title">推荐阅读</div>
        <div class="article-detail-recommend-list lr-page-container">
          <lr-link
            class="article-detail-recommend-item"
            v-for="item in recommendList"
            :key="item.id"
            :to="item.url"
            @click="reportClick({ click: 'recommend' })"
          >
            <p class="ellipsis-2">{{ item.title }}</p>
          </lr-link>
        </div>
      </div>

      <div class="article-detail-sidebar">
        <fav-btn
          class="article-detail-sidebar-item"
          :id="this.id"
          :isFav="isFav"
          @change="handleFav"
          @click.native="reportClick({ click: 'collect' })"
        >
          <div class="article-detail-sidebar-btn fav">
            <icon
              :type="`iconic_collect${
                isFav && this.$store.state.user.userInfo ? '_sel' : ''
              }`"
            ></icon>
          </div>
          <div class="article-detail-sidebar-txt">
            {{ data.post_favorites | crowd }}
          </div>
        </fav-btn>
        <like-btn
          class="article-detail-sidebar-item"
          :id="this.id"
          :isLike="isLike"
          @change="handleLike"
          @click.native="reportClick({ click: 'praise' })"
        >
          <div class="article-detail-sidebar-btn">
            <icon
              :type="`iconic_like${
                isLike && this.$store.state.user.userInfo ? '_sel' : ''
              }`"
            ></icon>
          </div>
          <div class="article-detail-sidebar-txt">
            {{ data.post_like | crowd }}
          </div>
        </like-btn>
        <div class="article-detail-sidebar-item">
          <div class="article-detail-sidebar-btn" @click="handleWechatShare">
            <icon type="iconic_share1"></icon>
          </div>
        </div>
        <div class="article-detail-sidebar-item" >
          <lr-link
            class="article-detail-sidebar-btn article-detail-sidebar-btn-red"
            to="https://pc-package.wpscdn.cn/wps/download/W.P.S.20.3041.exe"
            @click="reportClick({ click: 'download' })"
          >
            <icon type="iconic_download1" class="red-color"></icon>
          </lr-link>
        </div>
      </div>

      <deeplink-modal
        ref="deeplink"
        @close="
          $collect.popupClick({
            click: 'close',
            source: 'article',
            type: id,
          })
        "
      ></deeplink-modal>
      <wechat-share
        :shareUrl="data.wechat_link"
        ref="wechatShare"
      ></wechat-share>

      <feedback-help
        :id="Number(id)"
        :from-channel="3"
        sort="article"
        area="bottom_right"
      ></feedback-help>
    </template>

    <empty v-else></empty>
  </article>
</template>

<script>
import WechatShare from '@/components/wechat-share';
import FavBtn from '@/components/common-btns/fav-btn';
import LikeBtn from '@/components/common-btns/like-btn';
import DeeplinkModal from '@/components/deeplink-modal';
import Empty from '@/components/page-empty';
import FeedbackHelp from '@/components/common/feedback-help';
import LearningService from '@/plugins/services/learning';
import mixinAsyncData from '@/common/mixin/mixin-async-data';
import { isEleVisible, isInWps } from '@/common/utils';
import { FOOTER_HEIGHT } from '@/common/constant';
import clientApi from '@/common/utils/vba';

// 文章详情逗留30s后上报已读
let timeout = null;

async function asyncData({ app, params, route }) {
  const { id } = params;
  const { path } = route;
  const isPreview = path.includes('preview');

  const [res, opRes] = await Promise.all([
    app.$service.learning.getArticleDetail(id, isPreview),
    app.$service.learning.getArticleOpData(id),
  ]);

  const data = (res && res.data) || {};
  const opData = opRes && opRes.data;

  return { data: data.article, opData, id };
}

export default {
  head() {
    let header = this.$head.defaultHead();

    const { data } = this;
    const siteInfo = this.$store.state.common.siteInfo;

    const title = `${(data && data.post_title) || ''}-WPS学堂`;
    const keywords = `${data && data.post_keywords},${
      siteInfo.site_seo_keywords
    }`;
    const description = `${data && data.post_excerpt},${
      siteInfo.site_seo_description
    }`;

    let options = {
      title,
      keywords,
      description,
    };

    if (data) {
      header = this.$head.detailHead(options);
    }

    return header;
  },

  data() {
    return {
      paddingBottom: 480,
      isLike: 0,
      isFav: 0,

      data: null,
      id: null,

      hrefs: null, // 内容上所有的a标签元素
      // 已经上报后无需重复上报，避免滚动回调重复上报
      hasReport: false,
      // 逗留30s标志
      canReportByTime: false,
      // 滚动底部标志
      canReportByScroll: false,
    };
  },

  mixins: [mixinAsyncData(asyncData)],

  components: {
    WechatShare,
    FavBtn,
    LikeBtn,
    DeeplinkModal,
    Empty,
    FeedbackHelp,
  },

  computed: {
    reportSource({ $route }) {
      return $route.path.indexOf('simple') !== -1 ? 'simple' : 'routine';
    },

    isShowRecommend({ opData }) {
      if (!opData || !opData.relateds) return false;

      return opData.relateds.length >= 3 && opData.relateds.length <= 6;
    },

    recommendList({ opData }) {
      if (!opData || !opData.relateds) return [];

      const opDataLength = opData.relateds.length;

      if (opDataLength > 3 && opDataLength < 6) {
        return opData.relateds.slice(0, 3);
      } else {
        return opData.relateds;
      }
    },
  },

  methods: {
    handleWechatShare() {
      this.$refs.wechatShare.show = true;
      this.reportClick({ click: 'wechatshare' });
    },

    /**
     * 处理收藏
     */
    handleFav(status) {
      this.isFav = status;

      if (status) {
        this.data.post_favorites = +this.data.post_favorites + 1;
      } else {
        this.data.post_favorites = +this.data.post_favorites - 1;

        //避免因为页面缓存出现-1的状况
        if (this.data.post_favorites < 0) {
          this.data.post_favorites = 0;
        }
      }
    },

    /**
     * 处理点赞
     */
    handleLike(status) {
      this.isLike = status;
      if (status) {
        this.data.post_like = +this.data.post_like + 1;
      } else {
        this.data.post_like = +this.data.post_like - 1;
      }

      //避免因为页面缓存出现-1的状况
      if (this.data.post_like < 0) {
        this.data.post_like = 0;
      }
    },
    /**
     * 获取点赞、收藏状态
     */
    async getLikeFavState() {
      const res = await this.$service.learning.getLikeFavState(this.id);

      if (res && res.data) {
        this.isLike = res.data.is_like;
        this.isFav = res.data.is_fav;
      }
    },

    /**
     * 上报点击
     */
    reportClick({ click, ranking, order, listid, channel }) {
      this.$collect.detailsSkillarticleClick({
        click,
        ranking,
        order,
        listid,
        channel,
        type: this.id,
        source: this.reportSource,
      });
    },

    /**
     * 上报展示
     */
    reportShow({ show, order, ranking }) {
      this.$collect.detailsSkillarticleShow({
        show,
        order,
        type: this.id,
        ranking,
        source: this.reportSource,
      });
    },

    /**
     * 处理滚动时上报的逻辑
     */
    handleScrollReport() {
      const rect = document.body.getBoundingClientRect();
      const { innerHeight: windowHeight } = window;
      const scrollTop = Math.abs(rect.top);
      const extraOffsetTop = 50;

      // 上报浏览进度，同进度只上报一次
      const viewProgress = Math.ceil(
        ((scrollTop + windowHeight) / rect.height) * 100
      );
      if (!this._3progressShow && viewProgress >= 30 && viewProgress < 50) {
        // 浏览到30%
        this._3progressShow = true;
        this.reportShow({ show: '0.3' });
      } else if (
        !this._6progressShow &&
        viewProgress >= 50 &&
        viewProgress < 90
      ) {
        // 浏览到50%
        this._6progressShow = true;
        this.reportShow({ show: '0.5' });
      } else if (
        !this._9progressShow &&
        viewProgress >= 90 &&
        viewProgress < 100
      ) {
        // 浏览到90%
        this._9progressShow = true;
        this.reportShow({ show: '0.9' });
      } else if (!this._1progressShow && viewProgress >= 100) {
        // 浏览到100%
        this._1progressShow = true;
        this.reportShow({ show: '1' });
        this.canReportByScroll = true;
        // 文章滚动到底部，未上报并且用户已登录，上报文章已学
        if (
          !this.hasReport &&
          this.canReportByTime &&
          this.$store.state.user.userInfo
        ) {
          this.report();
        }
      }

      // 上报推荐位展示
      if (
        !this._recommendShow &&
        this.isShowRecommend &&
        isEleVisible({
          ele: this.$refs.recommend,
          windowHeight,
          scrollTop,
          extraOffsetTop,
        })
      ) {
        this._recommendShow = true;
        this.reportShow({ show: 'recommend' });
      }

      // 浏览到正文底部
      if (!this._isScrollBottom && rect.bottom - innerHeight <= FOOTER_HEIGHT) {
        this._isScrollBottom = true;
      }

      // 判断文章内的a标签是否展示
      const hrefs = this.hrefs;
      this._hrefShowMap = this._hrefShowMap || {};
      if (hrefs && Object.keys(this._hrefShowMap).length !== hrefs.length) {
        hrefs.forEach((item, index) => {
          if (
            !this._hrefShowMap[index] &&
            isEleVisible({ ele: item, windowHeight, scrollTop, extraOffsetTop })
          ) {
            this._hrefShowMap[index] = true;
            this.reportShow({ show: 'link', order: index + 1 });
          }
        });
      }
    },

    /**
     * 绑定内容链接上的事件
     */
    initContentLinksEvents() {
      this.hrefs =
        this.$refs.content && this.$refs.content.querySelectorAll('a');

      this.hrefs &&
        this.hrefs.forEach((item, index) => {
          const order = index + 1;
          item.addEventListener('click', (e) => {
            this.reportClick({ click: 'link', order });
            const href = item.href || '';

            // 拦截deeplink文章
            if (href.indexOf('dk.u.wps.cn') !== -1) {
              e.preventDefault();
              const source = 'article';
              const fromurl = encodeURIComponent(window.location.href);
              const number = index + 1;
              const type = this.id;
              /**
               * source: video | article | qa
               *  type: 教程id
               * number: 第n个位置
               */
              let queryStr = href.indexOf('?') !== -1 ? '&' : '?';
              queryStr += `source=${source}&type=${type}&number=${number}&fromurl=${fromurl}`;
              const url = href + queryStr;
              this.$refs.deeplink.wakeup(url);
            }
          });
        });
    },

    /**
     * 绑定内容视频上的事件
     */
    initContentVideoEvents() {
      let videos =
        this.$refs.content && this.$refs.content.querySelectorAll('video');

      videos &&
        videos.forEach((item, index) => {
          const ranking = index + 1;
          item.addEventListener('play', () =>
            this.reportClick({ click: 'playvideo', ranking })
          );

          item.addEventListener('pause', () =>
            this.reportClick({ click: 'suspendvideo', ranking })
          );

          item.addEventListener('ended', () =>
            this.reportClick({ click: 'endvideo', ranking })
          );
        });
    },

    /**
     * 初始化VBA事件
     */
    initVbaEvents() {
      const vbaBtn = document.querySelector('#btn_open_vba_model');

      if (!vbaBtn) return;

      vbaBtn.addEventListener('click', async (e) => {
        if (isInWps()) {
          // 端内劫持点击事件
          e.preventDefault();
          // 未登录用户先登录
          if (!this.$store.state.user.userInfo) {
            this.$eventBus.$emit('login');
            return;
          }
          const REGISTER_CLIENT_URL =
            'https://plus.wps.cn/register?utm_source=pcoffice&utm_medium=vba&utm_buysource=vbalanding&hide_components=title';

          const BUY_URL =
            'https://plus.wps.cn/buy?utm_source=pcoffice&utm_medium=vba&utm_buysource=vbalanding&hide_components=title';

          await this.$store.dispatch('user/getUserInfo');

          const isPersonalUser = !this.$store.state.user.userInfo.companyid;
          // 企业/个人用户跳转链接不同，个人跳注册，企业跳购买
          clientApi.openTagByVbaPay(
            isPersonalUser ? REGISTER_CLIENT_URL : BUY_URL
          );
        }
      });
    },

    afterMounted() {
      this._enterTime = Date.now();

      this.paddingBottom = this.$refs.recommend
        ? this.$refs.recommend.clientHeight + 20
        : 0;

      this.getLikeFavState();

      // 上报访问
      if (this.data) {
        this.$service.learning.readPostItem(this.data.post_type, this.id);
      }

      this.reportShow({ show: 'details' });

      // 上报滚动触发的各种展示事件
      this.handleScrollReport();
      window.addEventListener('scroll', this.handleScrollReport);

      // 绑定a标签上的点击事件
      this.initContentLinksEvents();
      // 绑定视频上的点击事件
      this.initContentVideoEvents();

      // 初始化VBA事件
      this.initVbaEvents();

      // 页面离开时上报
      window.addEventListener('beforeunload', () => {
        if (this._isBeforeUnload) return;
        this._isBeforeUnload = true;
        this.$collect.detailsSkillarticleEnd({
          time: Math.ceil((Date.now() - this._enterTime) / 1000),
          type: this.id,
        });
      });
    },
    async report() {
      if (!this.$store.state.user.userInfo) {
        return;
      }
      const res = await LearningService.userpostread({
        post_type: this.data.post_type,
        post_id: Number(this.id),
      }).catch(() => null);
      res && (this.hasReport = true);
    },
  },

  mounted() {
    timeout = setTimeout(() => {
      this.canReportByTime = true;
      if (
        !this.hasReport &&
        this.canReportByScroll &&
        this.$store.state.user.userInfo
      ) {
        this.report();
      }
    }, 30000);
  },

  destroyed() {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
      this.canReportByTime = false;
      this.canReportByScroll = false;
    }
    window.removeEventListener('scroll', this.handleScrollReport);
  },
};
</script>

<style lang="scss" scoped>
.article-detail {
  position: relative;
  flex-grow: 1;

  &-main {
    width: 700px;
    margin: 0 auto 120px;
  }

  &-title {
    font-size: 24px;
    font-weight: 600;
    line-height: 33px;
    margin-bottom: 10px;
  }

  &-info {
    @include flex-align-center();
    font-size: 12px;
    font-weight: 400;
    color: #888888;
    line-height: 17px;
    margin-bottom: 30px;

    > span {
      margin-right: 40px;
    }

    &-platform {
      @include flex-align-center();

      > span:not(:first-of-type) {
        margin-left: 10px;
        padding-left: 10px;
        border-left: 1px solid #d8d8d8;
      }
    }
  }

  &-recommend {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fafbfd;
    padding: 80px 0 50px;

    &-title {
      font-size: 24px;
      font-weight: 500;
      line-height: 33px;
      text-align: center;
      margin-bottom: 40px;
    }

    &-list {
      display: flex;
      flex-wrap: wrap;
    }

    &-item {
      @include flex-center();
      width: 356px;
      height: 110px;
      background: #ffffff;
      border-radius: 4px;
      border: 1px solid #e8e8e8;
      box-sizing: border-box;
      margin-right: 20px;
      margin-bottom: 30px;
      padding: 0 68px;

      &:nth-of-type(3n) {
        margin-right: 0;
      }
    }
  }

  &-sidebar {
    position: fixed;
    top: 50%;
    left: calc(((100vw - 1110px) / 2) + 1110px + 20px);
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;

    &-item {
      margin-bottom: 20px;
      text-align: center;

      .fav {
        > svg {
          color: rgb(247, 201, 64);
        }
      }
    }

    &-btn {
      @include flex-center();
      width: 50px;
      height: 50px;
      border-radius: 25px;
      border: 1px solid #cecece;
      font-size: 24px;
      margin-bottom: 6px;
      background: #fff;
      cursor: pointer;
      &-red {
        border-color:#ff4500
      }
    }

    &-txt {
      font-size: 12px;
      color: #888888;
      line-height: 17px;
    }
    .red-color {
      color: #ff4500;
    }
  }

  @media screen and (max-width: 1400px) {
    .article-detail-sidebar {
      left: initial;
      right: 76px;
    }
  }

  @media screen and (max-width: 1036px) {
    .article-detail-sidebar {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.article-detail-content {
  img {
    max-width: 100% !important;
    height: auto !important;
  }
  p,
  span {
    font-family: '微软雅黑' !important;
  }
}
</style>
